import React from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';

// Helper function to safely format timestamps
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  // Check if it's a Firebase timestamp (has seconds and nanoseconds)
  if (timestamp.seconds && timestamp.nanoseconds) {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  }
  
  // Check if it's an ISO string
  if (typeof timestamp === 'string') {
    try {
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      return timestamp;
    }
  }
  
  return String(timestamp);
};

// Helper function to format signal strength
const formatSignalStrength = (strength) => {
  if (strength === undefined || strength === null) return 'N/A';
  
  const strengthValue = Number(strength);
  
  if (isNaN(strengthValue)) return 'N/A';
  
  switch (strengthValue) {
    case 1: return 'Low';
    case 2: return 'Medium';
    case 3: return 'High';
    default: return `${strengthValue}`;
  }
};

export default function ContactListTable({ 
  contacts, 
  jobs, 
  isLoading, 
  sortField, 
  sortDirection, 
  onSort 
}) {
  // Helper function to render sort indicator
  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ChevronUpIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
      : <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />;
  };
  
  // Helper function to handle sort click
  const handleSortClick = (field) => {
    if (onSort) {
      onSort(field);
    }
  };
  
  // Helper function to get job title from job ID
  const getJobTitle = (jobId) => {
    if (!jobId || !jobs[jobId]) return 'Unknown Job';
    return jobs[jobId].title || 'Untitled Job';
  };
  
  // Helper function to get job company from job ID
  const getJobCompany = (jobId) => {
    if (!jobId || !jobs[jobId]) return '';
    return jobs[jobId].company || '';
  };
  
  // Helper function to render jobs list
  const renderJobs = (jobIds) => {
    if (!jobIds || jobIds.length === 0) return <span className="text-gray-400">No jobs</span>;
    
    return (
      <div className="flex flex-col space-y-2">
        {jobIds.map((jobId, index) => (
          <div key={`${jobId}-${index}`} className="flex flex-col border-l-2 border-purple-200 pl-2">
            <span className="font-medium text-gray-900">{getJobTitle(jobId)}</span>
            {getJobCompany(jobId) && (
              <span className="text-xs text-gray-500">{getJobCompany(jobId)}</span>
            )}
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-300">
        <thead>
          <tr>
            <th 
              scope="col" 
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer"
              onClick={() => handleSortClick('firstName')}
            >
              <div className="group inline-flex items-center">
                Name
                {renderSortIndicator('firstName')}
              </div>
            </th>
            <th 
              scope="col" 
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
              onClick={() => handleSortClick('email')}
            >
              <div className="group inline-flex items-center">
                Email
                {renderSortIndicator('email')}
              </div>
            </th>
            <th 
              scope="col" 
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
              onClick={() => handleSortClick('company')}
            >
              <div className="group inline-flex items-center">
                Company
                {renderSortIndicator('company')}
              </div>
            </th>
            <th 
              scope="col" 
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
              onClick={() => handleSortClick('jobId')}
            >
              <div className="group inline-flex items-center">
                Job
                {renderSortIndicator('jobId')}
              </div>
            </th>
            <th 
              scope="col" 
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
              onClick={() => handleSortClick('signalStrength')}
            >
              <div className="group inline-flex items-center">
                Signal Strength
                {renderSortIndicator('signalStrength')}
              </div>
            </th>
            <th 
              scope="col" 
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
              onClick={() => handleSortClick('createdAt')}
            >
              <div className="group inline-flex items-center">
                Created
                {renderSortIndicator('createdAt')}
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {isLoading ? (
            <tr>
              <td colSpan="6" className="py-10 text-center text-gray-500">
                <div className="flex justify-center items-center">
                  <svg className="animate-spin h-5 w-5 text-purple-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading contacts...
                </div>
              </td>
            </tr>
          ) : contacts.length === 0 ? (
            <tr>
              <td colSpan="6" className="py-10 text-center text-gray-500">
                No contacts found
              </td>
            </tr>
          ) : (
            contacts.map((contact) => (
              <tr key={contact.id} className="hover:bg-gray-50">
                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                  {contact.firstName} {contact.lastName}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {contact.email || 'N/A'}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {contact.company || 'N/A'}
                </td>
                <td className="px-3 py-4 text-sm text-gray-500">
                  {renderJobs(contact.jobs || (contact.jobId ? [contact.jobId] : []))}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    contact.signalStrength === 3 
                      ? 'bg-green-100 text-green-800' 
                      : contact.signalStrength === 2 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-red-100 text-red-800'
                  }`}>
                    {formatSignalStrength(contact.signalStrength)}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {formatTimestamp(contact.createdAt)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
